import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ScrollMenu } from 'react-horizontal-scrolling-menu';
import 'react-horizontal-scrolling-menu/dist/styles.css';
import { useFilePicker } from 'use-file-picker';
import { t } from 'i18next';

import { useQueryParams } from '../../../utils/useQueryParams';
import { resizeToBase64 } from '../../../utils/resizeImage';
import isResizableMimetype from '../../../utils/ImageUtils';
import { isDomainValid } from '../../../utils/validateUrl';
import { getMemberIds } from '../../../utils/etcUtils';
import { requestFootprintChange } from '../../../data/repositories/RequestChange';

import StickyHeader from '../../../components/StickyHeader';
import AddFootprint from '../../../components/api/mutations/AddFootprint';
import StepComponent from '../../../components/steps/StepComponent';
import { ReportFootprintBreadcrumb } from '../../../components/report/ReportFootprintBreadcrumb';

import { Button, InputBoxSingle } from '@peacecover/component';
import { Xmark } from '@peacecover/icon';
import { ReactComponent as CameraIcon } from '../../../icons/camera.svg';
import { ReactComponent as CloseIcon } from '../../../icons/xmark.svg';

export default function AddReportFootprintPage() {
  const { updateQueryParams, queryParams, removeQueryParams } = useQueryParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const currentQueryParams = new URLSearchParams(search);
  const locationId = currentQueryParams.get('locationId');
  const artistId = currentQueryParams.get('artistId');
  const memberId = currentQueryParams.get('memberId');
  const eventId = currentQueryParams.get('eventId');
  const contentType = currentQueryParams.get('contentType');
  const locationName = currentQueryParams.get('locationName');
  const kakaoUID = currentQueryParams.get('kakaoUID');
  const siteType = currentQueryParams.get('spotsCategory');
  const footprintId = currentQueryParams.get('footprintId') || null;

  const [imageDataUrl, setImageDataUrl] = useState();
  const [isUploading, setIsUploading] = useState(false);
  const [description, setDescription] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [isLinkUrlValid, setIsLinkUrlValid] = useState(true);
  const { openFilePicker, filesContent, plainFiles } = useFilePicker({
    readAs: 'DataURL',
    accept: 'image/*',
    multiple: false
  });

  const parsedMemberIds = getMemberIds(memberId);

  useEffect(() => {
    if (filesContent && filesContent.length > 0) {
      const file = (plainFiles || [])[0];
      if (file && isResizableMimetype(file.type)) {
        resizeToBase64(file).then((dataUrl) => {
          setImageDataUrl(dataUrl);
          setCurrentStep(1);
        });
        return;
      }
      setImageDataUrl(filesContent[0]?.content);
      setCurrentStep(1);
    }
  }, [filesContent, plainFiles]);

  const submitChange = async () => {
    if (!footprintId) return;
    setIsUploading(true);
    try {
      const response = await requestFootprintChange({
        footprintId,
        artistId,
        category: siteType,
        contentType,
        description,
        linkUrl,
        memberIds: parsedMemberIds,
        dataUrls: [imageDataUrl]
      });
      if (response.ok) {
        navigate(`/report-location/events/complete-edit?${queryParams.toString()}`);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const submitCreate = async () => {
    setIsUploading(true);
    try {
      const safeFootprintableId = locationId && locationId !== 'null' ? locationId : undefined;
      const response = await AddFootprint({
        name: locationName,
        kakaoUID: kakaoUID,
        siteType: siteType,
        footprintableType: 'Location',
        footprintableId: safeFootprintableId,
        contentType: contentType,
        eventId: eventId,
        artistId: artistId,
        memberIds: parsedMemberIds,
        description: description,
        dataUrls: [imageDataUrl],
        linkUrl: linkUrl
      });
      if (response.ok) {
        const data = await response.json();
        window.opener?.postMessage({ type: 'report-location-complete' }, window.location.origin);
        updateQueryParams(
          { locationId: data.footprint.footprintableId },
          '/report-location/events/complete'
        );
      }
    } finally {
      setIsUploading(false);
    }
  };

  const onDone = async () => {
    if (!isLinkUrlValid) {
      alert('유효한 링크인지 확인 후 입력해 주세요');
      return;
    }
    if (isUploading) return;
    if (footprintId) {
      await submitChange();
    } else {
      await submitCreate();
    }
  };

  const handleAddImageClick = () => {
    openFilePicker();
  };

  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
    if (description) {
      setCurrentStep(3);
    }
  };

  const handleLinkUrlChange = (e) => {
    setLinkUrl(e.target.value);
    if (isDomainValid(e.target.value)) {
      setIsLinkUrlValid(true);
    } else {
      setIsLinkUrlValid(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>
          {t('AddReportFootprintPage.pageTitle', {
            defaultValue: '제보하기'
          })}
        </title>
      </Helmet>
      <StickyHeader
        title={t('AddReportFootprintPage.pageTitle', {
          defaultValue: '제보하기'
        })}
        showBackButton={false}
        alwaysShowTitle={true}
        leftContent={
          <Xmark
            size={24}
            className="text-text-main-lv1"
            onClick={() =>
              removeQueryParams(['contentType'], '/report-location/footprints-category')
            }
          />
        }
      />
      <div className="relative min-h-[100lvh] flex flex-col pt-[calc(54px+env(safe-area-inset-top))] pb-[env(safe-area-inset-bottom)]">
        <StepComponent currentStep={currentStep} nextStep={1}>
          <div className="flex flex-col px-[24px]">
            <div className="mx-[-24px]">
              <ReportFootprintBreadcrumb />
            </div>
            <div className="text-[26px] font-bold py-[10px] whitespace-pre-wrap text-pure-black dark:text-pure-white">
              {t('AddReportFootprintPage.postImageTitle', {
                defaultValue: '관련된 사진이나 화면 캡처가\n있다면 첨부해 주세요'
              })}
            </div>
            <div className="flex flex-col mt-[16px] py-[12px] gap-[8px]">
              <div className="text-[15px] font-semibold leading-[18px] text-text-main-lv1">
                {t('AddReportFootprintPage.postImageDescription', {
                  defaultValue: '캡쳐본이나 사진을 추가해 주세요!'
                })}
              </div>
              <div className="text-[14px] font-normal text-text-main-lv4">
                {t('AddReportFootprintPage.postImageSourceDescription', {
                  defaultValue: '인스타 화면, 방송 스샷, 목격 사진 등 어떤 자료든 좋아요!'
                })}
              </div>
            </div>
          </div>
          <div className="w-full">
            <ScrollMenu wrapperClassName="padding-left-scroll-container fixed-height-scroll-container">
              <button
                type="button"
                className="border border-separator-light-1 dark:border-separator-dark-1 w-[75px] h-[75px] flex flex-col justify-center items-center cursor-pointer mt-[12px] rounded-[12px]"
                onClick={handleAddImageClick}
              >
                <CameraIcon className="fill-black dark:fill-white" />
                <div className="text-[12px] text-sub-light-1 dark:text-sub-dark-1">
                  {imageDataUrl ? 1 : 0}/1
                </div>
              </button>
              {imageDataUrl && (
                <div className="w-[75px] h-[75px] relative ml-2 pt-3">
                  <img
                    src={imageDataUrl}
                    alt={'attachment'}
                    className="w-[75px] h-[75px] rounded-[8px] object-cover"
                  />
                  <button
                    type="button"
                    className="absolute top-[1px] right-[-10px] rounded-full p-1 cursor-pointer inline-flex bg-primary-light dark:bg-primary-dark"
                    onClick={() => setImageDataUrl(null)}
                  >
                    <CloseIcon width={'16px'} height={'16px'} className="fill-onPrimary " />
                  </button>
                </div>
              )}
            </ScrollMenu>
          </div>
        </StepComponent>
        <StepComponent currentStep={currentStep} nextStep={2}>
          <div className="flex flex-col gap-[4px] px-[24px] mt-[32px]">
            <div className="flex flex-col gap-[8px] py-[12px]">
              <p className="text-[17px] font-semibold leading-[20px] text-text-main-lv1">
                {t('AddReportFootprintPage.linkTitle', {
                  defaultValue: '덕메들이 참고할만한 링크가 있나요?'
                })}
              </p>
              <p className="text-[14px] leading-[17px] text-text-main-lv4">
                {t('AddReportFootprintPage.linkDescription', {
                  defaultValue: '장소가 언급되었던 라이브나 게시물의 링크도 좋아요!'
                })}
              </p>
              <div className="pt-[12px] pb-[5px] relative">
                <InputBoxSingle
                  type="url"
                  pattern="https?://.*"
                  className="bg-transparent"
                  placeholder={t('AddReportFootprintPage.linkPlaceholder', {
                    defaultValue: '내용'
                  })}
                  value={linkUrl}
                  onChange={handleLinkUrlChange}
                  maxLength={1000}
                />
                {!isLinkUrlValid && (
                  <p className="text-[11px] text-on-warning absolute bottom-[-15px]">
                    {t('AddReportFootprintPage.linkInvalid', {
                      defaultValue: '유효한 링크인지 확인 후 입력해 주세요'
                    })}
                  </p>
                )}
              </div>
            </div>
          </div>
        </StepComponent>
        <StepComponent currentStep={currentStep} nextStep={2}>
          <div className="px-[24px] pt-[12px]">
            <div className="pt-[12px] pb-[6px] text-[17px] font-semibold leading-[20px] text-text-main-lv1">
              {t('AddReportFootprintPage.descriptionTitle', {
                defaultValue: '추천 이유나 하고 싶은 얘기가 더 있나요?'
              })}
            </div>
            <textarea
              className="bg-secondary-light dark:bg-secondary-dark mt-[12px] rounded-[12px] w-full p-[20px] resize-y h-[80px] font-semibold placeholder:text-placeholder-light dark:placeholder:text-placeholder-dark outline-none"
              placeholder={t('AddReportFootprintPage.descriptionPlaceholder', {
                defaultValue: '여기를 눌러서 입력해 주세요'
              })}
              value={description}
              onChange={handleDescriptionChange}
            />
          </div>
        </StepComponent>

        {imageDataUrl && (
          <div
            className={
              'sticky bottom-0 left-0 right-0 bg-background-light dark:bg-background-dark text-center z-[9999] flex gap-[7px]'
            }
            style={{
              padding: '10px 24px calc(10px + env(safe-area-inset-bottom)) 24px'
            }}
          >
            <Button
              size="large"
              btnColor="primary"
              onClick={onDone}
              className="w-full text-[17px] font-semibold"
              disabled={isUploading}
            >
              {t('done')}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

